import { store } from '../store.js';
import {
  createEmptyDuringRally,
  createEmptyDuringRallyEvents
} from './useMigration.js';

/**
 * Current point composable — owns point state, actions, computed validations, rally logic.
 * Bridge pattern: receives component instance during Options API phase.
 */
export function useCurrentPoint(instance) {
  function updatePreviewScore() {
    const baseScore = instance.score || { won: 0, lost: 0 };
    const outcome = instance.currentPoint?.scoreAction?.outcome;
    if (outcome === 'Won') {
      instance.currentPoint.currentScore = { won: baseScore.won + 1, lost: baseScore.lost };
      return;
    }
    if (outcome === 'Lost') {
      instance.currentPoint.currentScore = { won: baseScore.won, lost: baseScore.lost + 1 };
      return;
    }
    instance.currentPoint.currentScore = { ...baseScore };
  }

  function updateCurrentPoint(key, value) {
    if (instance.matchFinished) {
      return;
    }

    const hasRatingOrCall = (value.rating !== null && value.rating !== undefined) ||
                            (value.call !== null && value.call !== undefined);

    if ((key === 'serveAction' || key === 'receiveAction') && hasRatingOrCall && instance.lastConfirmedPointId !== null) {
      console.log('Clearing lastConfirmedPointId in updateCurrentPoint', key, value);
      instance.lastConfirmedPointId = null;
    }

    if (key === 'serveAction') {
      instance.currentPoint.serveAction = { ...instance.currentPoint.serveAction, ...value };
    } else if (key === 'receiveAction') {
      instance.currentPoint.receiveAction = { ...instance.currentPoint.receiveAction, ...value };
    } else if (key === 'scoreAction') {
      instance.currentPoint.scoreAction = { ...instance.currentPoint.scoreAction, ...value };
      if (value.outcome === null || value.outcome === undefined) {
        instance.currentPoint.rallyActive = true;
      }
    } else {
      instance.currentPoint[key] = value;
    }
    if (key === 'startPoint') {
      refreshOnCourtPositions();
    }
  }

  function updateSelectedOption(option) {
    if (instance.matchFinished) {
      return;
    }
    if (!option) {
      return;
    }
    const isTieBreak = store.match.setsWon === 2 && store.match.setsLost === 2 && instance.currentSetNumber === 5;
    if (isTieBreak) {
      if (!instance.matchTiebreakServeReceive) {
        instance.matchTiebreakServeReceive = option;
      }
      return;
    }
    if (!instance.matchStartServeReceive) {
      instance.matchStartServeReceive = option;
    }
  }

  function undoInitialStartPointSelection() {
    if (instance.matchFinished) {
      return;
    }
    const isSetStartWithNoPoints = instance.currentPlayByPlay.length === 0;
    const isMatchStart = instance.currentSetNumber === 1;
    const isTieBreakStart = instance.currentSetNumber === 5 && instance.matchSetsWon === 2 && instance.matchSetsLost === 2;
    if (!(isSetStartWithNoPoints && (isMatchStart || isTieBreakStart))) {
      return;
    }
    instance.currentPoint.startPoint = '';
    if (isTieBreakStart) {
      instance.matchTiebreakServeReceive = '';
    } else {
      instance.matchStartServeReceive = '';
    }
    instance.startPointDisabled = false;
  }

  function updateDuringRally(payload) {
    if (instance.matchFinished) {
      return;
    }
    instance.currentPoint.duringRally = payload.counts;
    instance.currentPoint.duringRallyEvents = payload.events;
  }

  function syncRallyActive() {
    const startPoint = instance.currentPoint.startPoint;
    if (startPoint === 'Serve') {
      const { rating, call } = instance.currentPoint.serveAction;
      instance.currentPoint.rallyActive = rating !== null && rating !== 0 && rating !== 4 && !call;
      return;
    }
    if (startPoint === 'Receive') {
      const { rating, call } = instance.currentPoint.receiveAction;
      instance.currentPoint.rallyActive = rating !== null && rating !== 0 && !call;
      return;
    }
    instance.currentPoint.rallyActive = false;
  }

  function handleRallyBack() {
    instance.isUndoing = true;

    if (instance.currentPoint.startPoint === 'Serve') {
      instance.currentPoint.serveAction = {
        ...instance.currentPoint.serveAction,
        rating: null,
        call: null
      };
    } else if (instance.currentPoint.startPoint === 'Receive') {
      instance.currentPoint.receiveAction = {
        player: {},
        rating: null,
        call: null
      };
    }

    instance.currentPoint.duringRally = createEmptyDuringRally();
    instance.currentPoint.duringRallyEvents = createEmptyDuringRallyEvents();
    instance.currentPoint.scoreAction = {
      outcome: null,
      pointDetail: '',
      player: {},
      secondPlayer: null
    };
    instance.currentPoint.rallyActive = false;

    instance.$nextTick(() => {
      instance.isUndoing = false;
    });
  }

  function resetCurrentPointState(disableStartPoint = true) {
    instance.currentPoint.serveAction = { player: {}, rating: null, call: null };
    instance.currentPoint.receiveAction = { player: {}, rating: null, call: null };
    instance.currentPoint.scoreAction = { outcome: null, pointDetail: '', player: {}, secondPlayer: null };
    instance.currentPoint.duringRally = createEmptyDuringRally();
    instance.currentPoint.duringRallyEvents = createEmptyDuringRallyEvents();
    instance.currentPoint.duringRallyRoster = {};
    instance.currentPoint.rallyActive = false;
    instance.scoreActionCollapsed = true;

    instance.$nextTick(() => {
      if (instance.$refs.serveAction && typeof instance.$refs.serveAction.resetSelectedPlayer === 'function') {
        instance.$refs.serveAction.resetSelectedPlayer();
      }
      if (instance.$refs.receiveAction && typeof instance.$refs.receiveAction.resetSelectedPlayer === 'function') {
        instance.$refs.receiveAction.resetSelectedPlayer();
      }
    });

    if (disableStartPoint) {
      instance.startPointDisabled = true;
    }
  }

  function resetCurrentPointView() {
    instance.currentPoint.serveAction = { player: {}, rating: null, call: null };
    instance.currentPoint.receiveAction = { player: {}, rating: null, call: null };
    instance.currentPoint.scoreAction = { outcome: null, pointDetail: '', player: {}, secondPlayer: null };
    instance.currentPoint.duringRally = createEmptyDuringRally();
    instance.currentPoint.duringRallyEvents = createEmptyDuringRallyEvents();
    instance.currentPoint.duringRallyRoster = {};
    instance.currentPoint.rallyActive = false;
    instance.startPointDisabled = instance.matchFinished;
    if (instance.$refs.serveAction) {
      instance.$refs.serveAction.resetSelectedPlayer();
    }
    if (instance.$refs.receiveAction) {
      instance.$refs.receiveAction.resetSelectedPlayer();
    }
  }

  function incrementCurrentPointValues() {
    if (instance.currentPoint.scoreAction.outcome === 'Won' && instance.currentPoint.startPoint === 'Receive') {
      const rotationMap = {
        'P1': 'P6',
        'P2': 'P1',
        'P3': 'P2',
        'P4': 'P3',
        'P5': 'P4',
        'P6': 'P5'
      };
      instance.currentPoint.currentRotationCode = rotationMap[instance.currentPoint.currentRotationCode];
    }

    instance.currentPoint.id += 1;

    if (instance.currentPoint.scoreAction.outcome === 'Won') {
      instance.currentPoint.startPoint = 'Serve';
    } else if (instance.currentPoint.scoreAction.outcome === 'Lost') {
      instance.currentPoint.startPoint = 'Receive';
    }

    const sortedPlayers = store.currentLineup.slice().sort((a, b) => a.rotationPosition - b.rotationPosition);
    instance.currentLineup = sortedPlayers;
    instance.currentPoint.currentLineupNumbers = sortedPlayers.map(player => player.player.number);
    instance.currentPoint.currentOnCourtNumbers = instance.applyLiberoSubstitution(instance.currentPoint.currentLineupNumbers);
  }

  function buildDuringRallyRosterMap() {
    const map = {};
    instance.currentLineup.forEach(item => {
      if (item.player && item.player.number !== undefined) {
        map[item.player.number] = item.player.name || '';
      }
    });
    if (
      instance.currentPoint.currentLiberoNumber &&
      store.currentLibero &&
      store.currentLibero.number === instance.currentPoint.currentLiberoNumber
    ) {
      map[store.currentLibero.number] = store.currentLibero.name || '';
    }
    return map;
  }

  function refreshOnCourtPositions() {
    if (!Array.isArray(instance.currentPoint.currentLineupNumbers) || instance.currentPoint.currentLineupNumbers.length < 6) {
      return;
    }
    instance.currentPoint.currentOnCourtNumbers = instance.applyLiberoSubstitution(instance.currentPoint.currentLineupNumbers);
  }

  return {
    updatePreviewScore,
    updateCurrentPoint,
    updateSelectedOption,
    undoInitialStartPointSelection,
    updateDuringRally,
    syncRallyActive,
    handleRallyBack,
    resetCurrentPointState,
    resetCurrentPointView,
    incrementCurrentPointValues,
    buildDuringRallyRosterMap,
    refreshOnCourtPositions
  };
}
